<script lang="ts" setup>
import type {
  DataSourceEntry,
  Page,
  ProductListingFilter,
  ProductPage,
} from "@/types";

const { blok } = defineProps<{ blok: ProductListingFilter }>();

await usePriceColorEntries();

type BlokVersion = "draft" | "published";

const perPage = 6;
const isMounted = ref(false);
const products = ref<Page<ProductPage>[]>([]);
const filters = ref<DataSourceEntry[]>([]);
const currentPage = ref<number>(1);
const hasMore = ref<boolean>(true);
const isLoading = ref<boolean>(false);

const config = useRuntimeConfig();
const apiInstance = useStoryblokApi();

const loadProducts = async () => {
  isLoading.value = true;

  if (currentPage.value === 1) {
    hasMore.value = true;
    products.value = [];
  }

  let filterQuery: Record<string, any> = {};

  const typeFilters = [
    ...filters.value
      .filter(({ key }: any) => key === "type")
      .map(({ value }: any) => value),
    blok.productType,
  ].filter(Boolean);

  if (typeFilters.length > 0) {
    filterQuery = { ...filterQuery, type: { in: typeFilters.join(",") } };
  }

  const useFilters = [
    ...filters.value
      .filter(({ key }: any) => key === "use")
      .map(({ value }: any) => value),
    blok.use,
  ].filter(Boolean);
  if (useFilters.length > 0) {
    filterQuery = { ...filterQuery, use: { in: useFilters.join(",") } };
  }

  const priceFilters = [
    ...filters.value
      .filter(({ key }: any) => key === "price")
      .map(({ value }: any) => value),
    blok.price,
  ].filter(Boolean);
  if (priceFilters.length > 0) {
    filterQuery = { ...filterQuery, price: { in: priceFilters.join(",") } };
  }

  const backgroundFilters = [
    ...filters.value
      .filter(({ key }: any) => key === "backgroundColor")
      .map(({ value }: any) => value),
    blok.backgroundColor,
  ].filter(Boolean);
  if (backgroundFilters.length > 0) {
    filterQuery = {
      ...filterQuery,
      backgroundColor: { in: backgroundFilters.join(",") },
    };
  }

  const designFilters = [
    ...filters.value
      .filter(({ key }: any) => key === "designFeatures")
      .map(({ value }: any) => value),
    blok.designFeature,
  ].filter(Boolean);
  if (designFilters.length > 0) {
    filterQuery = {
      ...filterQuery,
      designFeatures: { in: designFilters.join(",") },
    };
  }

  const data = await apiInstance
    .get("cdn/stories", {
      content_type: "product-page",
      per_page: perPage,
      page: currentPage.value,
      version: config.public?.storyblok?.version as BlokVersion,
      sort_by: "content.price:asc,content.name:asc",
      filter_query: filterQuery,
    })
    .finally(() => (isLoading.value = false));

  const fetchedPages = data.data.stories;
  products.value = [...products.value, ...fetchedPages];
  hasMore.value = data.total / perPage > currentPage.value;
};

const handleFilter = (selected: DataSourceEntry[]) => {
  filters.value = selected;
  currentPage.value = 1;
  loadProducts();
};

onMounted(() => {
  // loadProducts();
  isMounted.value = true;
});

const loadMore = () => {
  if (!hasMore.value) return;

  currentPage.value++;
  loadProducts();
};
</script>

<template>
  <Container v-editable="blok" class="relative py-5 bg-white">
    <ProductFilter
      class="sticky top-0 z-20"
      :pre-filters="blok"
      @filter="handleFilter"
    />
    <div class="py-5">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:gap-10 gap-5"
      >
        <ProductListingSummaryItem
          v-for="page in products"
          :key="page.id"
          :slug="page.full_slug"
          :product="page.content"
          :uuid="page.uuid"
        />
      </div>

      <RichText
        v-if="
          blok.emptyText && products.length === 0 && isMounted && !isLoading
        "
        v-editable="blok"
        class="py-5"
        :content="blok.emptyText"
      />

      <div v-if="hasMore && isMounted" class="mt-10 text-center py-10">
        <UButton
          class="bg-black text-white px-12 py-5 hover:bg-black disabled:bg-black"
          :ui="{ rounded: 'rounded-full' }"
          :loading="isLoading"
          @click="loadMore"
        >
          {{ isLoading ? "Loading" : "Load More" }}
        </UButton>
      </div>
    </div>
  </Container>
</template>
